/**
 * Tabela wierszy z GET /kontrola-korekt/lista.
 *
 * Kolumny (kontrakt): Status, Numer, Data, Sklep, Brutto, Faktura korygowana
 * (numer + odznaka zrodla/pewnosci; przy kilku kandydatkach select), Pozycje
 * (dopasowane/wszystkie), Akcje: "Podgląd" (modal), "Dodaj korektę" (tylko 'gotowa',
 * potwierdzenie INLINE -> wynik KFZ + WZ), "Pomiń" (z powodem), "Przywróć",
 * "PDF" (podglad PDF korekty w nowej karcie, gdy wiersz.pdf) i "Pobierz PDF z portalu"
 * (brak faktury zrodlowej bez zadnego pliku -> wydruk z portalu SFD + przeliczenie).
 *
 * `AkcjeDodaj` jest wspolny dla tabeli i stopki modalu (ten sam flow).
 * Kontener ma overflow: auto - tabela nie rozpycha strony.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { kontrolaKorektService } from '../../services/kontrolaKorektService';
import {
  STATUSY_KOREKT, ZRODLA_FAKTURY, PEWNOSC, STATUS_DO_DODANIA, STATUSY_DO_POMINIECIA,
  formatujDate, formatujKwote, komunikatBleduAkcji,
} from './format';

const th = (tekst, extra = {}) => (
  <th key={tekst} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', ...extra }}>{tekst}</th>
);
const td = { padding: '5px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', fontSize: '11px' };

const btnMaly = (tlo, kolor, ramka) => ({
  padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap',
  border: `1px solid ${ramka}`, background: tlo, color: kolor,
});
const btnWylaczony = { ...btnMaly('var(--bg)', 'var(--text-muted)', 'var(--border)'), cursor: 'not-allowed' };
const pole = { padding: '4px 8px', fontSize: '11px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' };

export const ZnacznikStatusuKorekty = ({ status }) => {
  const s = STATUSY_KOREKT[status] || { etykieta: status || '—', kolor: 'var(--text-secondary)', tlo: 'var(--bg)' };
  return (
    <span data-testid="status" style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', color: s.kolor, backgroundColor: s.tlo }}>
      {s.etykieta}
    </span>
  );
};

/** Odznaka "z PDF" / "z XML" / "domyślnie: najnowsza pasująca" / "wybrana ręcznie" w kolorze pewnosci. */
export const OdznakaZrodlaFaktury = ({ zrodlo, pewnosc }) => {
  const etykieta = ZRODLA_FAKTURY[zrodlo];
  if (!etykieta) return null;
  const p = PEWNOSC[pewnosc] || { etykieta: null, kolor: 'var(--text-secondary)', tlo: 'var(--bg)' };
  return (
    <span data-testid="odznaka-zrodla" title={p.etykieta || undefined}
      style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap', color: p.kolor, backgroundColor: p.tlo }}>
      {etykieta}
    </span>
  );
};

/**
 * Kolumna "Faktura korygowana": numer (link do /purchase-invoices/<id>, gdy w systemie) + odznaka;
 * gdy wiersz ma kilka kandydatek (pewnosc 'niska') - select z wyborem recznym (POST /<id>/faktura).
 */
export const FakturaKorygowana = ({ wiersz, onWybierzFakture, wybieranie }) => {
  const f = wiersz?.faktura;
  const kandydaci = Array.isArray(wiersz?.kandydaci) ? wiersz.kandydaci : [];
  if (!f && kandydaci.length === 0) {
    return <span data-testid="faktura-korygowana" style={{ color: wiersz?.status === 'brak_faktury' ? 'var(--danger)' : 'var(--text-muted)' }}>—</span>;
  }
  const numer = f?.numer ? (
    f.w_systemie && f.id
      ? <Link to={`/purchase-invoices/${f.id}`} data-testid="link-faktura" title={`Otwórz fakturę #${f.id}`} style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>{f.numer}</Link>
      : <span style={{ fontWeight: 600, whiteSpace: 'nowrap', color: f.w_systemie === false ? 'var(--warning)' : 'var(--text)' }} title={f.w_systemie === false ? 'Faktury nie ma w systemie' : undefined}>{f.numer}</span>
  ) : null;
  return (
    <span data-testid="faktura-korygowana" style={{ display: 'inline-block', lineHeight: 1.3 }}>
      {numer}
      {f?.w_systemie === false && <span style={{ display: 'block', fontSize: '10px', color: 'var(--warning)' }}>brak w systemie</span>}
      {f && <span style={{ display: 'block' }}><OdznakaZrodlaFaktury zrodlo={f.zrodlo} pewnosc={f.pewnosc} /></span>}
      {kandydaci.length > 1 && onWybierzFakture && (
        <select
          aria-label="Wybierz fakturę korygowaną"
          data-testid="select-kandydatki"
          value={f?.id ? String(f.id) : ''}
          disabled={wybieranie}
          onChange={(e) => { if (e.target.value) onWybierzFakture(wiersz, Number(e.target.value)); }}
          style={{ ...pole, display: 'block', marginTop: '3px', maxWidth: '220px' }}
        >
          <option value="">— wybierz kandydatkę —</option>
          {kandydaci.map(k => (
            <option key={k.faktura_id} value={String(k.faktura_id)}>
              {k.numer} · {formatujDate(k.data)}{k.pokrycie !== undefined && k.pokrycie !== null ? ` · pokrycie ${Math.round(Number(k.pokrycie) * 100)}%` : ''}
            </option>
          ))}
        </select>
      )}
    </span>
  );
};

/**
 * "Dodaj korektę" z potwierdzeniem inline ("Utworzy korektę KFZ i WZ zdejmujący stan w sklepie X")
 * -> POST /<id>/dodaj -> wynik: numer KFZ + WZ (+ ostrzezenie o roznicy kwot). Aktywne tylko dla 'gotowa'.
 * Uzywane w tabeli i w stopce modalu.
 */
export const AkcjeDodaj = ({ wiersz, onDodano, kompakt = true }) => {
  const [potwierdzanie, setPotwierdzanie] = useState(false);
  const [wysylanie, setWysylanie] = useState(false);
  const [wynik, setWynik] = useState(null);
  const [blad, setBlad] = useState(null);
  const gotowa = wiersz?.status === STATUS_DO_DODANIA;
  const sklep = wiersz?.sklep?.nazwa || (wiersz?.sklep?.id ? `#${wiersz.sklep.id}` : 'nieznany');

  const dodaj = async () => {
    setWysylanie(true);
    setBlad(null);
    try {
      const dane = await kontrolaKorektService.dodaj(wiersz.zrodlo_id);
      setWynik(dane || {});
      setPotwierdzanie(false);
      toast.success(`Dodano korektę ${dane?.korekta_numer || ''}${dane?.wz_numer ? ` i WZ ${dane.wz_numer}` : ''}`.trim());
      if (dane?.ostrzezenie) toast(dane.ostrzezenie, { icon: '⚠️' });
      if (onDodano) onDodano(dane, wiersz);
    } catch (err) {
      if (!err?.isAuthError) setBlad(komunikatBleduAkcji(err, 'Nie udało się dodać korekty.'));
    } finally {
      setWysylanie(false);
    }
  };

  if (wynik) {
    return (
      <span data-testid="dodaj-wynik" style={{ display: 'inline-block', lineHeight: 1.3, color: 'var(--success)', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {wynik.korekta_id
          ? <Link to={`/purchase-invoices/${wynik.korekta_id}`} style={{ color: 'var(--success)' }}>KFZ {wynik.korekta_numer || `#${wynik.korekta_id}`}</Link>
          : <>KFZ {wynik.korekta_numer || '—'}</>}
        {wynik.wz_numer && <span style={{ display: 'block' }}>WZ {wynik.wz_numer}</span>}
        {wynik.ostrzezenie && <span data-testid="dodaj-ostrzezenie" style={{ display: 'block', color: 'var(--warning)', fontWeight: 400, whiteSpace: 'normal' }}>{wynik.ostrzezenie}</span>}
      </span>
    );
  }

  if (potwierdzanie) {
    return (
      <span data-testid="dodaj-potwierdzenie" style={{ display: 'inline-flex', flexDirection: 'column', gap: '4px', maxWidth: kompakt ? '260px' : 'none' }}>
        <span style={{ color: 'var(--warning)', fontWeight: 600 }}>Utworzy korektę KFZ i WZ zdejmujący stan w sklepie {sklep}</span>
        <span style={{ display: 'flex', gap: '6px' }}>
          <button type="button" data-testid="btn-dodaj-potwierdz" onClick={dodaj} disabled={wysylanie}
            style={{ ...btnMaly('var(--success)', '#fff', 'var(--success)'), opacity: wysylanie ? 0.6 : 1 }}>
            {wysylanie ? 'Dodaję…' : 'Tak, dodaj'}
          </button>
          <button type="button" data-testid="btn-dodaj-anuluj" onClick={() => { setPotwierdzanie(false); setBlad(null); }} disabled={wysylanie}
            style={btnMaly('var(--card-bg)', 'var(--text)', 'var(--border)')}>
            Anuluj
          </button>
        </span>
        {blad && <span data-testid="dodaj-blad" style={{ color: 'var(--danger)', whiteSpace: 'normal' }}>{blad}</span>}
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '4px' }}>
      <button type="button" data-testid="btn-dodaj-korekte" disabled={!gotowa}
        onClick={() => setPotwierdzanie(true)}
        title={gotowa ? `Utworzy korektę KFZ i WZ w sklepie ${sklep}` : 'Dodać można tylko korektę ze statusem „Do dodania”'}
        style={gotowa ? btnMaly('var(--success)', '#fff', 'var(--success)') : btnWylaczony}>
        Dodaj korektę
      </button>
      {blad && <span data-testid="dodaj-blad" style={{ color: 'var(--danger)', whiteSpace: 'normal' }}>{blad}</span>}
    </span>
  );
};

/** "Pomiń" z powodem (inline) -> POST /<id>/pomin; "Przywróć" -> POST /<id>/przywroc. */
const AkcjePomin = ({ wiersz, onZmiana }) => {
  const [pomijanie, setPomijanie] = useState(false);
  const [powod, setPowod] = useState('');
  const [wysylanie, setWysylanie] = useState(false);

  const wykonaj = async (fn, komunikat) => {
    setWysylanie(true);
    try {
      const dane = await fn();
      toast.success(komunikat);
      setPomijanie(false);
      if (onZmiana) onZmiana(dane, wiersz);
    } catch (err) {
      if (!err?.isAuthError) toast.error(komunikatBleduAkcji(err));
    } finally {
      setWysylanie(false);
    }
  };

  if (wiersz.status === 'pominieta') {
    return (
      <button type="button" data-testid="btn-przywroc" disabled={wysylanie}
        onClick={() => wykonaj(() => kontrolaKorektService.przywroc(wiersz.zrodlo_id), `Przywrócono korektę ${wiersz.numer}`)}
        style={btnMaly('var(--card-bg)', 'var(--primary)', 'var(--primary)')}>
        Przywróć
      </button>
    );
  }
  if (!STATUSY_DO_POMINIECIA.has(wiersz.status)) return null;
  if (pomijanie) {
    return (
      <span data-testid="pomin-formularz" style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="text" aria-label="Powód pominięcia" value={powod} onChange={(e) => setPowod(e.target.value)} placeholder="Powód pominięcia" maxLength={255}
          disabled={wysylanie} style={{ ...pole, minWidth: '160px' }} />
        <button type="button" data-testid="btn-pomin-potwierdz" disabled={wysylanie}
          onClick={() => wykonaj(() => kontrolaKorektService.pomin(wiersz.zrodlo_id, powod.trim()), `Pominięto korektę ${wiersz.numer}`)}
          style={btnMaly('var(--text-secondary)', '#fff', 'var(--text-secondary)')}>
          {wysylanie ? 'Pomijam…' : 'Pomiń'}
        </button>
        <button type="button" onClick={() => setPomijanie(false)} disabled={wysylanie} style={btnMaly('var(--card-bg)', 'var(--text)', 'var(--border)')}>Anuluj</button>
      </span>
    );
  }
  return (
    <button type="button" data-testid="btn-pomin" onClick={() => setPomijanie(true)} title="Pomiń tę korektę (z powodem)"
      style={btnMaly('var(--card-bg)', 'var(--text-secondary)', 'var(--border)')}>
      Pomiń
    </button>
  );
};

/** Podglad PDF korekty (z maila albo z portalu) w nowej karcie - GET plik/pdf jako blob (z sesja). */
export const otworzPdfKorekty = async (wiersz) => {
  try {
    const blob = await kontrolaKorektService.pobierzPdf(wiersz.zrodlo_id);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
  } catch (err) {
    if (!err?.isAuthError) toast.error(komunikatBleduAkcji(err, 'Nie udało się otworzyć PDF.'));
  }
};

/** Statusy, przy ktorych wydruk z portalu ma co poprawic: faktura korygowana nieustalona. */
const bezFakturyZrodlowej = (wiersz) => wiersz?.status === 'brak_faktury' || (wiersz?.status === 'brak_produktow' && !wiersz?.faktura);

/**
 * "PDF" (wiersz.pdf) -> podglad w nowej karcie; "Pobierz PDF z portalu" (brak_faktury bez PDF) ->
 * POST /<id>/pdf-z-portalu: zapis wydruku + przeliczenie (numer faktury korygowanej z PDF) -> onZmiana(szczegoly, wiersz).
 */
export const AkcjePdf = ({ wiersz, onZmiana, onPdf }) => {
  const [pobieranie, setPobieranie] = useState(false);
  if (wiersz?.pdf) {
    const zrodlo = wiersz.pdf_zrodlo === 'portal' ? 'wydruk z portalu SFD' : 'PDF z maila';
    return (
      <button type="button" data-testid="btn-pdf" onClick={() => (onPdf || otworzPdfKorekty)(wiersz)}
        title={`Podgląd korekty ${wiersz.numer || ''} (${zrodlo}) — otwiera się w nowej karcie`}
        style={btnMaly('var(--bg)', 'var(--text-primary)', 'var(--border)')}>
        PDF
      </button>
    );
  }
  if (!bezFakturyZrodlowej(wiersz)) return null;
  const pobierz = async () => {
    setPobieranie(true);
    try {
      const dane = await kontrolaKorektService.pobierzPdfZPortalu(wiersz.zrodlo_id);
      const numer = dane?.faktura?.numer;
      toast.success(numer ? `PDF z portalu pobrany — faktura korygowana ${numer}` : `PDF z portalu pobrany dla ${wiersz.numer}`);
      if (onZmiana) onZmiana(dane, wiersz);
    } catch (err) {
      if (!err?.isAuthError) toast.error(komunikatBleduAkcji(err, 'Nie udało się pobrać PDF z portalu.'));
    } finally {
      setPobieranie(false);
    }
  };
  return (
    <button type="button" data-testid="btn-pdf-z-portalu" disabled={pobieranie} onClick={pobierz}
      title={wiersz.pdf_blad
        ? `Ostatnia próba nie powiodła się: ${wiersz.pdf_blad}. Kliknij, aby spróbować ponownie.`
        : 'Pobierze wydruk korekty z portalu SFD i odczyta z niego numer faktury korygowanej'}
      style={{ ...btnMaly('var(--card-bg)', wiersz.pdf_blad ? 'var(--warning)' : 'var(--primary)', wiersz.pdf_blad ? 'var(--warning)' : 'var(--primary)'), opacity: pobieranie ? 0.6 : 1 }}>
      {pobieranie ? 'Pobieram PDF…' : 'Pobierz PDF z portalu'}
    </button>
  );
};

/** Kolumna "Akcje": Podgląd | Dodaj korektę | Pomiń / Przywróć | PDF / Pobierz PDF z portalu. */
export const AkcjeKorekty = ({ wiersz, onPodglad, onDodano, onZmiana, onPdf }) => (
  <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>
    <button type="button" data-testid="btn-podglad" onClick={() => onPodglad && onPodglad(wiersz)}
      style={btnMaly('var(--card-bg)', 'var(--primary)', 'var(--primary)')}>
      Podgląd
    </button>
    {wiersz.status !== 'dodana' && wiersz.status !== 'pominieta' && <AkcjeDodaj wiersz={wiersz} onDodano={onDodano} />}
    <AkcjePomin wiersz={wiersz} onZmiana={onZmiana} />
    <AkcjePdf wiersz={wiersz} onZmiana={onZmiana} onPdf={onPdf} />
  </span>
);

/** Kolumna "Korekta w systemie" (status 'dodana'): KFZ (link) + WZ. */
const KorektaWSystemie = ({ korekta }) => {
  if (!korekta) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  return (
    <span data-testid="korekta-w-systemie" style={{ display: 'inline-block', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
      {korekta.id
        ? <Link to={`/purchase-invoices/${korekta.id}`} style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>{korekta.numer || `#${korekta.id}`}</Link>
        : <span style={{ fontWeight: 600 }}>{korekta.numer || '—'}</span>}
      {korekta.wz && <span style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)' }}>WZ: {korekta.wz}</span>}
    </span>
  );
};

/**
 * Props: wiersze, ladowanie, onPodglad(wiersz), onDodano(wynik, wiersz), onZmiana(wynik, wiersz) (pomin/przywroc/wybor
 * faktury/PDF z portalu), onPdf(wiersz)? (podglad PDF - domyslnie nowa karta).
 * Wybor kandydatki: POST /<id>/faktura, 400 (inny sklep) -> toast z komunikatem backendu.
 */
const TabelaKorekt = ({ wiersze, ladowanie, onPodglad, onDodano, onZmiana, onPdf }) => {
  const lista = Array.isArray(wiersze) ? wiersze : [];
  const [wybieranieId, setWybieranieId] = useState(null);

  const wybierzFakture = async (wiersz, fakturaId) => {
    setWybieranieId(wiersz.zrodlo_id);
    try {
      const dane = await kontrolaKorektService.wybierzFakture(wiersz.zrodlo_id, fakturaId);
      toast.success(`Wybrano fakturę ${dane?.faktura_numer || `#${fakturaId}`} dla ${wiersz.numer}`);
      if (onZmiana) onZmiana(dane, wiersz);
    } catch (err) {
      if (!err?.isAuthError) toast.error(komunikatBleduAkcji(err, 'Nie udało się wybrać faktury.'));
    } finally {
      setWybieranieId(null);
    }
  };

  return (
    <div data-testid="tabela-korekt" style={{ overflow: 'auto', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)', zIndex: 1 }}>
          <tr>
            {th('Status')}
            {th('Numer')}
            {th('Data')}
            {th('Sklep')}
            {th('Brutto', { textAlign: 'right' })}
            {th('Faktura korygowana')}
            {th('Pozycje', { textAlign: 'right' })}
            {th('Korekta w systemie')}
            {th('Akcje')}
          </tr>
        </thead>
        <tbody>
          {lista.map((w, idx) => {
            const brakiPozycji = Number(w.dopasowanych) < Number(w.pozycji);
            return (
              <tr key={`${w.zrodlo_id}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                <td style={td}>
                  <ZnacznikStatusuKorekty status={w.status} />
                  {w.komunikat && <span data-testid="komunikat" style={{ display: 'block', marginTop: '2px', fontSize: '10px', color: 'var(--text-muted)', maxWidth: '220px' }}>{w.komunikat}</span>}
                </td>
                <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text)' }}>
                  {w.numer}
                  {w.pdf && <span data-testid="znacznik-pdf" title={w.pdf_zrodlo === 'portal' ? 'Jest PDF — wydruk z portalu SFD' : 'Jest PDF z maila'} style={{ marginLeft: '6px', fontSize: '10px', padding: '1px 6px', borderRadius: '8px', color: 'var(--purple)', backgroundColor: 'var(--purple-light)' }}>PDF</span>}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatujDate(w.data)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600, color: w.sklep?.nazwa ? 'var(--text)' : 'var(--warning)' }}>{w.sklep?.nazwa || 'nieznany adres'}</td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--orange)' }} title={w.netto !== undefined && w.netto !== null ? `netto ${formatujKwote(w.netto)}` : undefined}>{formatujKwote(w.brutto)}</td>
                <td style={td}><FakturaKorygowana wiersz={w} onWybierzFakture={wybierzFakture} wybieranie={wybieranieId === w.zrodlo_id} /></td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, color: brakiPozycji ? 'var(--danger)' : 'var(--text)' }} data-testid="pozycje">
                  {w.pozycji === null || w.pozycji === undefined ? '—' : `${w.dopasowanych ?? 0}/${w.pozycji}`}
                </td>
                <td style={td}><KorektaWSystemie korekta={w.korekta} /></td>
                <td style={td}><AkcjeKorekty wiersz={w} onPodglad={onPodglad} onDodano={onDodano} onZmiana={onZmiana} onPdf={onPdf} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!ladowanie && lista.length === 0 && (
        <div data-testid="pusta-lista" style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
          Brak korekt dla wybranego filtru
        </div>
      )}
      {ladowanie && (
        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>Ładowanie…</div>
      )}
    </div>
  );
};

export default TabelaKorekt;
